<template>
    <div>
        <FieldLabel
            :label="label"
            :label-tooltip="labelTooltip"
            :disabled="disabled"
            :optional="optional"
            :status="status"
        />
        <div class="RPDropdown">
            <VuelidateWrapper
                :name="name"
                :hide-validation-status="hideValidationStatus"
                :value="value"
                :status="status"
            >
                <template v-slot:default="{ internalStatus, statusMessage }">
                    <ul :class="containerClass(internalStatus)">
                        <slot
                            :toggleOpen="toggleOpen"
                            :open="open"
                        >
                            <DropdownButton
                                :open="open"
                                :multiple="multiple"
                                :disabled="disabled"
                                :placeholder="placeholder"
                                :selected="selected"
                                :status="internalStatus"
                                :status-message="statusMessage"
                                @toggle-open="toggleOpen"
                                @deselect="deselectOption"
                            />
                        </slot>
                        <template v-if="open">
                            <DropdownOption
                                v-for="opt in optionsFormatted"
                                :key="opt.value"
                                :value="opt.value"
                                :label="opt.label"
                                :icon="opt.icon"
                                :color="opt.color"
                                :description="opt.description"
                                :disabled="opt.disabled"
                                :show-checkbox="multiple"
                                :selected="isSelected(opt.value)"
                                @select="selectOption"
                                @deselect="deselectOption"
                            />
                        </template>
                    </ul>
                </template>
            </VuelidateWrapper>
        </div>
        <RPModal
            v-if="showModal && open"
            :header="placeholder"
            :show-action-buttons="multiple"
            :show-cancel="false"
            submit-button-text="Select"
            @close-modal="open = false"
            @submit-modal="open = false"
        >
            <template slot="modalBody">
                <ul class="RPDropdown__modal">
                    <DropdownOption
                        v-for="opt in optionsFormatted"
                        :key="opt.value"
                        :value="opt.value"
                        :label="opt.label"
                        :icon="opt.icon"
                        :color="opt.color"
                        :description="opt.description"
                        :disabled="opt.disabled"
                        :show-checkbox="multiple"
                        :selected="isSelected(opt.value)"
                        @select="selectOption"
                        @deselect="deselectOption"
                    />
                </ul>
            </template>
        </RPModal>
    </div>
</template>

<script>
import FieldLabel from 'components/Basic/Elements/FieldLabel.vue';
import DropdownButton from './DropdownButton.vue';
import statusBuilder from 'status/statusBuilder';
import stat from 'status/index';
import {has, cloneDeep} from 'lodash';
import VuelidateWrapper from '../VuelidateWrapper';
import DropdownOption from "./DropdownOption.vue";

export default {
    name: "RPDropdown",
    components: {
        FieldLabel,
        DropdownButton,
        VuelidateWrapper,
        DropdownOption
    },
    props: {
        /**
         * A label to display above the dropdown
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Text to display in an informational tooltip next to the label.
         */
        labelTooltip: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false
        },
        optional: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Select Option...'
        },
        name: {
            type: String,
            default: '',
        },
        /**
         * @model
         */
        value: {
            required: true,
            // Workaround to allow null
            validator: prop => true
        },
        /**
         * An array of any type of values to be used as options.
         */
        options: {
            type: Array,
            required: true,
        },
        /**
         * A status pulled from the common `status` constants (`/src/status/index.js`)
         */
        modalBreakpoint: {
            type: Number,
            default: 320,
        },
        status: {
            type: String,
            default: stat.STANDBY
        }
    },
    data() {
        return {
            open: false,
            hideValidationStatus: true,
            size: 1000
        };
    },
    computed: {
        optionsFormatted(){
            return this.options.map(opt => {
                return typeof opt !== 'object'
                    ? {value: opt, label: String(opt),}
                    : opt;
            });
        },
        hasValidations(){
            return has(this.value, '$model');
        },
        val(){
            return this.hasValidations ? this.value.$model : this.value;
        },
        multiple(){
            return Array.isArray(this.val);
        },
        selected(){
            return this.multiple
                ? this.optionsFormatted.filter(opt => this.val.includes(opt.value))
                : this.optionsFormatted.find(opt => opt.value === this.val);
        },
        showModal() {
            return this.size < this.modalBreakpoint;
        },
    },
    destroyed(){
        document.removeEventListener('click', this.close);
        window.removeEventListener('resize', this.resize);
    },
    mounted() {
        document.addEventListener('click', this.close);
        window.addEventListener('resize', this.resize);
        this.resize();
    },
    methods: {
        resize(){
            this.size = Math.min(window.innerWidth, window.screen.width);
        },
        close(e){
            if(!this.$el.contains(e.target)){
                this.hideValidationStatus = false;
                this.open = false;
            }
        },
        toggleOpen(){
            if(this.open){
                this.hideValidationStatus = false;
            }
            this.open = !this.open;
        },
        isPrimitive(val) {
            return typeof val !== 'object' || val === null;
        },
        containerClass(internalStatus){
            return statusBuilder(
                'RPDropdown__container',
                internalStatus,
                this.disabled,
                {open: this.open}
            );
        },
        isSelected(opt){
            return this.multiple
                ? this.val.findIndex(v => v === opt) !== -1
                : this.val === opt;
        },
        input(val){
            if(this.hasValidations) {
                this.value.$model = val;
            } else {
                this.$emit('input', val);
            }
        },
        selectOption(value){
            let v = value;
            if(this.multiple){
                v = cloneDeep(this.val);
                v.push(value);
            } else{
                this.toggleOpen();
            }
            this.input(v);
        },
        deselectOption(value){
            if(this.multiple){
                this.input(this.val.filter(v => v !== value));
            } else{
                this.input(null);
                this.toggleOpen();
            }
        },
    }
};
</script>

<style lang="scss">
.RPDropdown {
    min-height: 2rem;
    min-width: 10rem;
    position: relative;

    &__container {
        overflow-y: auto;
        max-height: 25rem;
        width: 100%;
        list-style: none;
        position: absolute;
        border-radius: 4px;
        border: 1px solid $grey-100;
        background: $white;
        margin: 0;
        padding: 0;
        z-index: 99;
        right: 0;

        &--open {
            border-color: $primary;
            z-index: 100;
        }
        @include loading(2rem, 100%);
        &--success {
            border-color: $success-200;
            background: $success-50;
        }
        &--error {
            border-color: $error-200;
            background: $error-50;
        }
        &--warning {
            border-color: $warning-200;
            background: $warning-50;
        }

        &--opened {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        &--disabled {
            color: $grey-100;
            background: $grey-50;
        }
    }
    &__modal{
        list-style: none;
        background: $white;
        margin: 0;
        padding: 0;
    }
}
</style>

<docs>
```vue
<RPDropdown
    v-model="myVal"
    label="Example Label"
    label-tooltip="Example Tooltip"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<pre>{{ {"v-model": myVal} }}</pre>
```
Pass an array to `v-model` to create a multiselect. When no items are selected, the value of `v-model` will be an empty array rather than `null`.
```vue
<RPDropdown
    v-model="myValArray"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<pre>{{ {"v-model": myValArray} }}</pre>
```
```vue
import { FruitApple, FruitBanana } from "@reviewpush/streamline-regular/lib/food-drinks/Fruits";
<RPDropdown
    v-model="myValArray"
    placeholder="Pick a fruit:"
    :options="[
        {value: 'apple', label: 'Apple', description: 'Crunchy and tart', icon: FruitApple},
        {value: 'banana', label: 'Banana', description: 'Soft and sweet', icon: FruitBanana}
    ]"
/>
<pre>{{ {"v-model": myValArray} }}</pre>
<RPDropdown
    v-model="myVal"
    placeholder="Pick a fruit:"
    :options="[
        {value: 'apple', label: 'Apple', icon: FruitApple, color: 'red'},
        {value: 'banana', label: 'Banana', icon: FruitBanana, color: 'yellow'}
    ]"
/>
<pre>{{ {"v-model": myVal} }}</pre>
```
```vue
<RPDropdown
    v-model="myValArray"
    status="loading"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<br>
<RPDropdown
    v-model="myValArray"
    disabled
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<br>
<RPDropdown
    v-model="myValArray"
    status="success"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
<br>
<RPDropdown
    v-model="myValArray"
    status="error"
    :options="[
        'Adipiscing nascetur nostra',
        'Est condimentum feugiat',
        'Ridiculus sociis blandit',
    ]"
/>
```
Validation Example
```[import](./RPDropdownExample.vue)
```
</docs>